import Button from '@superfunnel/ui-core/v2/Button';
import Label from '@superfunnel/ui-core/v2/Label';
import Switch from '@superfunnel/ui-core/v2/Switch';
import {
  ToggleGroup,
  ToggleGroupItem,
} from '@superfunnel/ui-core/v2/ToggleGroup';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@superfunnel/ui-core/v2/Select';
import { Tabs, TabsList, TabsTrigger } from '@superfunnel/ui-core/v2/Tabs';

import DashboardAnalyticsRangeSelector from '@/components/DashboardAnalyticsRangeSelector';
import { EditorPresence } from '@/components/Presence/EditorPresence';
import { useAgentChat } from '@/contexts/AgentChatContext';
import { useAnalytics } from '@/contexts/AnalyticsContext';
import Badge from '@superfunnel/ui-core/v2/Badge';
import { Separator } from '@superfunnel/ui-core/v2/Separator';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@superfunnel/ui-core/v2/Tooltip';
import { classNames } from '@superfunnel/utils';
import {
  ArrowLeft,
  Computer,
  EditPencil,
  Eye,
  Keyframes,
  Plus,
  Settings,
  SidebarCollapse,
  SmartphoneDevice,
} from 'iconoir-react';
import { Check, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { EditorDevice, useEditor } from './EditorContext';

const testsChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

export interface EditorHeaderProps {}

export const EditorHeader = () => {
  const { track } = useAnalytics();
  const { resetAgentSession } = useAgentChat();
  const {
    mode,
    sidebarCollapsed,
    setSidebarCollapsed,
    selectedWebsiteId,
    setSelectedWebsiteId,
    resetReady,
    device,
    setDevice,
    saveStatus,
    lastSavedAt,
    // GlobalSection-specific props
    unpublishedChanges,
    publishing,
    publish,
    discarding,
    setDiscardChangesConfirmModalOpen,
    canRevert,
    canForward,
    revertLatestUpdate,
    forwardRevert,
    sidebarOpen,
    setSidebarOpen,
    setActiveWebsiteUpdate,
    agentHasUnreviewedChanges,
    setPendingAgentChangesModalOpen,
    presenceUsers,
    dynamicPageVariants,
    previewMode,
    setPreviewMode,
    setSettingsModalOpen,
    dynamicPage,
    dynamicPageId,
    onClose,
    showAnalytics,
    setShowAnalytics,
    isLoadingAnalytics,
  } = useEditor();

  const toggleSidebar = () => {
    const newState = !sidebarCollapsed;
    track('editor_sidebar_toggled', {
      collapsed: newState,
      mode,
    });
    setSidebarCollapsed(newState);
  };

  const handleOpenSectionsList = () => {
    track('editor_sections_list_opened', {
      mode,
    });

    // Clear any active section update to trigger list mode
    setActiveWebsiteUpdate(undefined);
    // Open the sidebar
    setSidebarOpen(!sidebarOpen);
  };

  const handleCloseEditor = () => {
    if (agentHasUnreviewedChanges) {
      track('editor_close_blocked_unreviewed_changes', {
        action: 'close_editor',
      });
      setPendingAgentChangesModalOpen({ open: true });
      return;
    }
    track('editor_closed', {
      mode,
    });
    onClose?.();
  };

  const handleOpenSettings = () => {
    if (!dynamicPageId || !dynamicPage) return;

    track('editor_page_settings_clicked', {
      dynamic_page_id: dynamicPageId,
    });
    setSettingsModalOpen({
      open: true,
      dynamicPageId: dynamicPageId,
      dynamicPage: dynamicPage,
    });
  };

  // Relative time for last saved
  const [relativeTime, setRelativeTime] = useState<string>('');

  // Update relative time every 30 seconds
  useEffect(() => {
    if (!lastSavedAt) {
      setRelativeTime('');
      return;
    }

    const updateTime = () => {
      const now = new Date();
      const diffMs = now.getTime() - lastSavedAt.getTime();
      const diffSec = Math.floor(diffMs / 1000);
      const diffMin = Math.floor(diffSec / 60);
      const diffHour = Math.floor(diffMin / 60);

      if (diffSec < 60) {
        setRelativeTime('just now');
      } else if (diffMin < 60) {
        setRelativeTime(`${diffMin} min ago`);
      } else if (diffHour < 24) {
        setRelativeTime(`${diffHour} hour${diffHour > 1 ? 's' : ''} ago`);
      } else {
        setRelativeTime(lastSavedAt.toLocaleDateString());
      }
    };

    updateTime();
    const interval = setInterval(updateTime, 30000);
    return () => clearInterval(interval);
  }, [lastSavedAt]);

  const saving = saveStatus === 'saving';

  return (
    <>
      <div className="fixed top-0 right-0 left-0 z-50 flex items-center justify-start">
        <div
          className={classNames(
            'tansition-all flex items-center justify-between gap-3 bg-[#F7F5FF] py-2 duration-300 ease-in-out',
            sidebarCollapsed
              ? 'w-0 overflow-hidden px-1 opacity-0'
              : 'w-[384px] px-2',
          )}
        >
          <div className="flex-1 shrink overflow-hidden">
            <Tooltip disableHoverableContent>
              <TooltipTrigger asChild>
                <Button
                  variant="default"
                  color="white"
                  size="icon-lg"
                  autoFocus={false}
                  onClick={handleCloseEditor}
                >
                  <ArrowLeft className="size-3.5" stroke="2" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">Back</TooltipContent>
            </Tooltip>
          </div>

          <Tooltip disableHoverableContent>
            <TooltipTrigger asChild>
              <Button
                variant="default"
                color="white"
                size="sm"
                onClick={() => {
                  track('editor_new_chat_clicked', {
                    mode,
                  });
                  resetAgentSession();
                }}
                title="Reset Agent Session"
              >
                <Plus className="size-4.5" />
                <span className="text-xs font-medium">New Chat</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>New Chat</p>
            </TooltipContent>
          </Tooltip>
        </div>

        <div className="flex grow items-center justify-between gap-3 bg-white py-1 pe-2">
          <div
            className={classNames(
              'flex items-center justify-start gap-1',
              sidebarCollapsed ? '' : 'px-1',
            )}
          >
            {sidebarCollapsed && (
              <>
                <Tooltip disableHoverableContent>
                  <TooltipTrigger asChild>
                    <Button
                      variant="default"
                      color="white"
                      size="icon-lg"
                      onClick={handleCloseEditor}
                    >
                      <ArrowLeft className="size-4" strokeWidth={2} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Back</TooltipContent>
                </Tooltip>

                {mode !== 'globalSection' && (
                  <Separator orientation="vertical" className="h-6" />
                )}
              </>
            )}
            {mode !== 'globalSection' && (
              <Button
                variant="default"
                color="white"
                size="icon-lg"
                onClick={toggleSidebar}
              >
                <SidebarCollapse
                  className={`h-5 w-5 transition-transform duration-200 ${sidebarCollapsed ? 'rotate-180' : ''}`}
                />
              </Button>
            )}
            {mode !== 'globalSection' && (
              <>
                <Separator orientation="vertical" className="h-6" />

                <Button
                  variant="default"
                  color="white"
                  size="icon-lg"
                  onClick={handleOpenSectionsList}
                >
                  <Keyframes className="h-5 w-5 rotate-90" />
                </Button>
                <Button
                  variant="default"
                  color="white"
                  size="icon-lg"
                  onClick={handleOpenSettings}
                >
                  <Settings className="h-5 w-5" />
                </Button>
                <div className="pointer-events-auto z-50">
                  <Select
                    value={selectedWebsiteId}
                    onValueChange={(websiteId) => {
                      if (websiteId === selectedWebsiteId) return;

                      track('editor_variant_switched', {
                        from_website_id: selectedWebsiteId,
                        to_website_id: websiteId,
                        mode,
                      });

                      // Reset agent chat to get fresh context for the new variant
                      resetAgentSession();

                      resetReady();
                      setSelectedWebsiteId(websiteId);
                    }}
                  >
                    <SelectTrigger className="min-w-[180px]" size="sm">
                      <SelectValue placeholder="Variant 1" />
                    </SelectTrigger>
                    <SelectContent>
                      {dynamicPageVariants?.flatMap((variant) =>
                        variant
                          .data()
                          .websiteIds.map((websiteId, testIndex) => (
                            <SelectItem key={websiteId} value={websiteId}>
                              {variant.data().name}
                              {variant.data().websiteIds.length > 1 && (
                                <Badge
                                  size="sm"
                                  variant={
                                    testIndex !== 0 ? 'warning' : 'purple'
                                  }
                                >
                                  {testIndex === 0
                                    ? 'Control'
                                    : variant.data().websiteIds.length === 2
                                      ? 'Experiment'
                                      : `Experiment ${testsChars[testIndex - 1] ?? testsChars[testsChars.length - 1]}`}
                                </Badge>
                              )}
                            </SelectItem>
                          )),
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {/* Save Status Indicator */}
                <div
                  className={classNames(
                    'h-8 w-22 gap-1',
                    saving || lastSavedAt
                      ? 'flex items-center'
                      : 'pointer-events-none invisible',
                  )}
                >
                  <Separator orientation="vertical" className="ms-2 h-6" />
                  <div className="flex items-center gap-1.5 px-2 text-sm text-gray-500">
                    {saving ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        <span className="animate-pulse">Saving...</span>
                      </>
                    ) : (
                      <Tooltip disableHoverableContent>
                        <TooltipTrigger asChild>
                          <div className="flex cursor-default items-center gap-1.5 transition-opacity duration-300">
                            <Check className="h-3.5 w-3.5 text-green-600" />
                            <span className="text-green-600">Saved</span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>Saved {relativeTime}</TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
          <div className="flex gap-2">
            <Tabs
              value={device}
              onValueChange={(t) => {
                const newDevice = t as EditorDevice;
                track('editor_device_changed', {
                  from_device: device,
                  to_device: newDevice,
                  mode,
                });
                setDevice(newDevice);
              }}
            >
              <TabsList>
                <TabsTrigger value="desktop">
                  <Computer
                    className={classNames(
                      'text-base-500 h-4 w-4',
                      device === 'desktop' && 'text-base-900',
                    )}
                  />
                </TabsTrigger>
                <TabsTrigger value="tablet">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8 14.6667V12M2 12H14M12.6667 1.33332H3.33333C2.59695 1.33332 2 1.93028 2 2.66666L2 13.3333C2 14.0697 2.59695 14.6667 3.33333 14.6667H12.6667C13.403 14.6667 14 14.0697 14 13.3333V2.66666C14 1.93028 13.403 1.33332 12.6667 1.33332Z"
                      stroke={device === 'tablet' ? '#141C25' : '#637083'}
                      strokeWidth="1.5"
                      strokeLinejoin="round"
                    />
                  </svg>
                </TabsTrigger>
                <TabsTrigger value="mobile">
                  <SmartphoneDevice
                    className={classNames(
                      'text-base-500 h-4 w-4',
                      device === 'mobile' && 'text-base-900',
                    )}
                  />
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <Tabs
              value={previewMode ? 'preview' : 'edit'}
              onValueChange={(t) => {
                const newPreviewMode = t === 'preview';
                track('editor_preview_mode_toggled', {
                  preview_mode: newPreviewMode,
                  mode,
                });
                setPreviewMode(newPreviewMode);
              }}
            >
              <TabsList>
                <TabsTrigger value="edit">
                  <EditPencil
                    className={classNames(
                      'size-4',
                      previewMode ? 'text-base-900' : 'text-base-500',
                    )}
                  />
                </TabsTrigger>
                <TabsTrigger value="preview">
                  <Eye
                    className={classNames(
                      'h-4 w-4',
                      previewMode ? 'text-base-900' : 'text-base-500',
                    )}
                  />
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <div className="flex items-center gap-2">
            {previewMode && (
              <>
                {showAnalytics && <DashboardAnalyticsRangeSelector />}
                <div className="flex items-center gap-2">
                  <Switch
                    checked={showAnalytics}
                    disabled={isLoadingAnalytics}
                    onCheckedChange={(v) => {
                      track('editor_analytics_toggled', {
                        show_analytics: v,
                        mode,
                      });
                      setShowAnalytics(v);
                    }}
                  />
                  <Label className="text-sm font-medium">Analytics</Label>
                </div>
                <Separator orientation="vertical" className="h-6" />
              </>
            )}
            {presenceUsers.length > 0 && (
              <div className="me-4">
                <EditorPresence users={presenceUsers} maxVisible={3} />
              </div>
            )}

            <ToggleGroup variant="outline" size={'sm'} type="multiple">
              <ToggleGroupItem
                value="bold"
                disabled={!canRevert}
                aria-label="Toggle bold"
                onClick={() => {
                  track('editor_undo_clicked', {
                    mode,
                  });
                  revertLatestUpdate();
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3.0013 5.33333H10.0013C10.0013 5.33333 13.3346 5.33333 13.3346 8.47059C13.3346 12 10.0013 12 10.0013 12H4.19178M5.0013 7.66667L2.66797 5.33333L5.0013 3"
                    stroke="#637083"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </ToggleGroupItem>
              <ToggleGroupItem
                value="italic"
                disabled={!canForward}
                aria-label="Toggle italic"
                onClick={() => {
                  track('editor_redo_clicked', {
                    mode,
                  });
                  forwardRevert();
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12.9987 5.33333H5.9987C5.9987 5.33333 2.66537 5.33333 2.66537 8.47059C2.66537 12 5.9987 12 5.9987 12H11.8082M10.9987 7.66667L13.332 5.33333L10.9987 3"
                    stroke="#637083"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </ToggleGroupItem>
            </ToggleGroup>

            {/* Publish/Discard buttons - for GlobalSection only */}
            {mode === 'globalSection' && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDiscardChangesConfirmModalOpen(true)}
                  disabled={!unpublishedChanges || publishing || discarding}
                >
                  {discarding ? 'Discarding...' : 'Discard'}
                </Button>
                <Button
                  size="sm"
                  onClick={() => {
                    if (selectedWebsiteId) {
                      publish(selectedWebsiteId);
                    }
                  }}
                  disabled={!unpublishedChanges || publishing || discarding}
                >
                  {publishing ? 'Publishing...' : 'Publish'}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
